import React, { useState, useEffect } from 'react';
import { collection, addDoc, setDoc, doc, Timestamp, serverTimestamp } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import SecureInput from '../common/SecureInput';
import { sanitizeString, sanitizeNumber } from '../../utils/sanitization';
import { useAuth } from '../auth/AuthContext';
import { collectionPath } from '../../utils/firestoreCollections';
import { usePermissions } from '../../hooks/usePermissions';
import { FEATURE_PERMISSIONS } from '../../utils/permissions';
import { convertDateForSave } from '../../utils/dateHelpers';
import { validateEntity } from '../../utils/schemaValidation';
import { handleFirestoreError, PerformanceMonitor } from '../../utils/errorHandling';
import { logEntityChanges } from '../../utils/logger';
import { getAssignmentUsers } from '../../utils/assignmentUsers';

// Helper to convert any date to yyyy-MM-dd for input[type="date"]
function toInputDate(val) {
    if (!val) return '';
    // If already in yyyy-MM-dd, return as is
    if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
    
    let d;
    // Handle Firestore Timestamp
    if (val.toDate && typeof val.toDate === 'function') {
        d = val.toDate();
    }
    // Handle Firestore Timestamp with seconds property
    else if (val.seconds) {
        d = new Date(val.seconds * 1000);
    }
    // Handle regular Date or string
    else {
        d = new Date(val);
    }
    
    if (!d || isNaN(d)) return '';
    
    // Use UTC methods to avoid timezone shifts
    const yyyy = d.getUTCFullYear();
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
}

const OrderModal = ({ user, initial, customers, onClose, onSaved, salesReps, users = [] }) => {
    const { currentUser } = useAuth();
    const { can } = usePermissions(user);
    const [orderNumber, setOrderNumber] = useState(initial?.orderNumber || '');
    const [customerId, setcustomerId] = useState(initial?.customerId || '');
    const [date, setDate] = useState(toInputDate(initial?.date) || new Date().toISOString().split('T')[0]);
    const [status, setStatus] = useState(initial?.status || 'Pending');
    const [items, setItems] = useState(initial?.items || '');
    const [total, setTotal] = useState(initial?.total || '');
    const [uid, setUid] = useState(initial?.uid || '');
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setOrderNumber(initial?.orderNumber || '');
        setcustomerId(initial?.customerId || '');
        setDate(toInputDate(initial?.date) || new Date().toISOString().split('T')[0]);
        setStatus(initial?.status || 'Pending');
        setItems(initial?.items || '');
        setTotal(initial?.total || '');
        setUid(initial?.uid || '');
    }, [initial]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!orderNumber || !customerId) {
            setError('Order number and customer are required');
            return;
        }
        setSaving(true);

        const enforcedUid = can(FEATURE_PERMISSIONS.ORDER_VIEW_ALL) ? uid : (initial?.uid || user?.uid || '');

        const data = { 
            orderNumber: sanitizeString(orderNumber, 100),
            customerId,
            date: convertDateForSave(date), // Convert to Timestamp
            status: sanitizeString(status, 50),
            items: sanitizeNumber(items, 0, 999999999),
            total: sanitizeNumber(total, 0, 999999999),
            uid: enforcedUid,
            updatedAt: serverTimestamp() // Auto-track when record was modified
        };
        
        // Add createdAt only for new orders
        if (!initial) {
            data.createdAt = serverTimestamp();
        }
        
        // Validate schema
        const validation = validateEntity(data, 'order');
        if (!validation.valid) {
            setError(validation.errors[0] || 'Invalid order data');
            setSaving(false);
            return;
        }
        
        const perfId = PerformanceMonitor.startOperation(
            initial ? 'update-order' : 'create-order'
        );

        try {
            if (initial && initial.id) {
                await setDoc(doc(db, collectionPath('orders'), initial.id), data);
                PerformanceMonitor.endOperation(perfId, true);
                logEntityChanges({
                    oldObj: initial,
                    newObj: data,
                    fields: ['orderNumber', 'customerId', 'date', 'status', 'items', 'total', 'uid'],
                    entityType: 'Order',
                    entityId: initial.id,
                    user: currentUser,
                    action: 'Updated',
                }).catch(e => console.warn('Failed to log order update:', e));
                onSaved('Order updated successfully');
            } else {
                const docRef = await addDoc(collection(db, collectionPath('orders')), data);
                PerformanceMonitor.endOperation(perfId, true);
                logEntityChanges({
                    oldObj: null,
                    newObj: data,
                    fields: ['orderNumber', 'customerId', 'date', 'status', 'items', 'total', 'uid'],
                    entityType: 'Order',
                    entityId: docRef.id,
                    user: currentUser,
                    action: 'Created',
                }).catch(e => console.warn('Failed to log order creation:', e));
                onSaved('Order added successfully');
            }
        } catch (err) {
            PerformanceMonitor.endOperation(perfId, false);
            console.error('Failed to save order:', err);
            const friendlyMsg = handleFirestoreError(
                err,
                initial ? 'update-order' : 'create-order',
                { orderNumber: data.orderNumber }
            );
            setError(friendlyMsg);
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg w-full max-w-md space-y-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{initial ? 'Edit' : 'Add'} Order</h2>
                {error && <p className="text-red-500 dark:text-red-400">{error}</p>}
                
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Order #</label>
                    <input className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 p-2 rounded" placeholder="Order #" value={orderNumber} onChange={e => setOrderNumber(e.target.value)} />
                </div>
                
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Customer</label>
                    <select className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 p-2 rounded" value={customerId} onChange={e => setcustomerId(e.target.value)} required>
                        <option value="">Select a customer</option>
                        {[...customers].sort((a, b) => a.name.localeCompare(b.name)).map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </select>
                </div>
                
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date</label>
                    <input className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 p-2 rounded" type="date" value={date} onChange={e => setDate(e.target.value)} />
                </div>
                
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                    <select className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 p-2 rounded" value={status} onChange={e => setStatus(e.target.value)}>
                        <option>Pending</option>
                        <option>Shipped</option>
                        <option>Cancelled</option>
                        <option>Returned</option>
                        <option>Complete</option>
                    </select>
                </div>
                
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Items</label>
                    <input className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 p-2 rounded" type="number" placeholder="Items" value={items} onChange={e => setItems(e.target.value)} />
                </div>
                
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Total</label>
                    <input className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 p-2 rounded" type="number" placeholder="Total" value={total} onChange={e => setTotal(e.target.value)} />
                </div>
                
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sales Rep</label>
                    {can(FEATURE_PERMISSIONS.ORDER_VIEW_ALL) ? (
                        <select
                            className="w-full border p-2 rounded"
                            value={uid}
                            onChange={e => setUid(e.target.value)}
                        >
                            <option value="">Unassigned</option>
                            {getAssignmentUsers(users, initial?.id ? initial.uid : '').map(u => (
                                <option key={u.uid || u.id} value={u.uid || u.id} disabled={u.role === 'disabled'}>
                                    {u.displayName || u.name || u.email || u.uid || u.id}{u.role === 'disabled' ? ' (Deactivated)' : ''}
                                </option>
                            ))}
                        </select>
                    ) : (
                        <input
                            className="w-full border p-2 rounded bg-gray-100"
                            value={
                                users.find(u => u.uid === (initial?.uid || user?.uid))?.name ||
                                users.find(u => u.uid === (initial?.uid || user?.uid))?.displayName ||
                                users.find(u => u.uid === (initial?.uid || user?.uid))?.email ||
                                initial?.uid || user?.uid || ''
                            }
                            disabled
                            readOnly
                        />
                    )}
                </div>
                
                <div className="flex justify-end space-x-2">
                    <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-gray-100 rounded">Cancel</button>
                    <button type="submit" disabled={saving} className="px-4 py-2 bg-indigo-600 dark:bg-indigo-500 text-white rounded">{saving ? 'Saving...' : 'Save'}</button>
                </div>
            </form>
        </div>
    );
};

export default OrderModal;
